import React, { useEffect, useState } from 'react'
import { X, Clock, CheckCircle, ChefHat, Package } from 'lucide-react'
import { OrderNotification, NotificationType } from '../types/websocket'

interface OrderNotificationToastProps {
  notification: OrderNotification
  onClose: () => void
  duration?: number // Auto-close duration in ms
}

const getNotificationIcon = (type: NotificationType) => {
  switch (type) {
    case NotificationType.PREPARATION_STARTED:
      return <ChefHat className="w-6 h-6 text-orange-500" />
    case NotificationType.ORDER_READY:
      return <CheckCircle className="w-6 h-6 text-green-500" />
    case NotificationType.TIME_UPDATE:
      return <Clock className="w-6 h-6 text-blue-500" />
    case NotificationType.ORDER_CONFIRMED:
      return <Package className="w-6 h-6 text-purple-500" />
    default:
      return <Clock className="w-6 h-6 text-gray-500" />
  }
}

const getNotificationColor = (type: NotificationType) => {
  switch (type) {
    case NotificationType.PREPARATION_STARTED:
      return 'from-orange-500 to-red-500'
    case NotificationType.ORDER_READY:
      return 'from-green-500 to-emerald-500'
    case NotificationType.TIME_UPDATE:
      return 'from-blue-500 to-cyan-500'
    case NotificationType.ORDER_CONFIRMED:
      return 'from-purple-500 to-pink-500'
    default:
      return 'from-gray-500 to-gray-600'
  }
}

export const OrderNotificationToast: React.FC<OrderNotificationToastProps> = ({
  notification,
  onClose,
  duration = 5000
}) => {
  const [progress, setProgress] = useState(100)
  const [isVisible, setIsVisible] = useState(true)

  // Play notification sound when component mounts
  useEffect(() => {
    const playNotificationSound = () => {
      try {
        // Use Web Audio API for notification sound
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
        const oscillator = audioContext.createOscillator()
        const gainNode = audioContext.createGain()

        oscillator.connect(gainNode)
        gainNode.connect(audioContext.destination)

        // Different tones for different notification types
        if (notification.type === NotificationType.ORDER_READY) {
          // Happy sound for ready order (higher pitch)
          oscillator.frequency.value = 800
          gainNode.gain.setValueAtTime(0.3, audioContext.currentTime)
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3)
          oscillator.start(audioContext.currentTime)
          oscillator.stop(audioContext.currentTime + 0.3)
        } else {
          // Standard notification sound
          oscillator.frequency.value = 520
          gainNode.gain.setValueAtTime(0.2, audioContext.currentTime)
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2)
          oscillator.start(audioContext.currentTime)
          oscillator.stop(audioContext.currentTime + 0.2)
        }
      } catch (error) {
        console.log('Could not play notification sound:', error)
      }
    }

    playNotificationSound()
  }, [notification.type])

  useEffect(() => {
    if (notification.type === NotificationType.ORDER_READY) {
      // Don't auto-close "order ready" notifications
      return
    }

    const startTime = Date.now()
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100)
      setProgress(remaining)

      if (remaining === 0) {
        setIsVisible(false)
        setTimeout(onClose, 300) // Wait for fade-out animation
      }
    }, 50)

    return () => clearInterval(interval)
  }, [duration, notification.type, onClose])

  const handleClose = () => {
    setIsVisible(false)
    setTimeout(onClose, 300)
  }

  return (
    <div
      className={`transform transition-all duration-300 ${
        isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
      }`}
    >
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden max-w-md w-full">
        {/* Progress bar */}
        <div className="h-1 bg-gray-200 dark:bg-gray-700">
          <div
            className={`h-full bg-gradient-to-r ${getNotificationColor(notification.type)} transition-all duration-100`}
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Content */}
        <div className="p-4">
          <div className="flex items-start gap-3">
            {/* Icon */}
            <div className="flex-shrink-0 mt-0.5">
              {getNotificationIcon(notification.type)}
            </div>

            {/* Message */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                {notification.message}
              </p>

              {/* Remaining time */}
              {notification.remainingTime !== undefined && notification.remainingTime > 0 && (
                <div className="flex items-center gap-2 mt-2">
                  <Clock className="w-4 h-4 text-gray-500" />
                  <div className="flex-1">
                    <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
                      <span>Tempo rimanente</span>
                      <span className="font-semibold text-orange-600 dark:text-orange-400">
                        {notification.remainingTime} min
                      </span>
                    </div>
                    {/* Progress bar for remaining time */}
                    {notification.estimatedPreparationTime && (
                      <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-orange-500 to-red-500 transition-all duration-1000"
                          style={{
                            width: `${
                              ((notification.estimatedPreparationTime - notification.remainingTime) /
                                notification.estimatedPreparationTime) *
                              100
                            }%`
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Order ready message */}
              {notification.type === NotificationType.ORDER_READY && (
                <p className="text-xs text-green-600 dark:text-green-400 mt-2 font-medium">
                  🎉 Puoi ritirare il tuo ordine!
                </p>
              )}

              {/* Timestamp */}
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                {new Date(notification.timestamp).toLocaleTimeString('it-IT', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>

            {/* Close button */}
            <button
              onClick={handleClose}
              className="flex-shrink-0 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
